/**
 * Email Link (Passwordless) Authentication Service
 *
 * Provides Firebase email link sign-in functionality alongside wallet authentication.
 *
 * Flow:
 * 1. User requests sign-in link via sendEmailSignInLink()
 * 2. User clicks link in email, redirected to /auth/email-callback
 * 3. completeEmailSignIn() is called, which:
 *    a. Signs in with email link (gets Firebase UID like "L6aJZVqvOcPzNK4r8mwQxY1iDfH3")
 *    b. Calls createEmailUserToken Cloud Function to get custom token with e_ prefix
 *    c. Signs in with custom token (now UID = "e_L6aJZVqvOcPzNK4r8mwQxY1iDfH3")
 * 4. This ensures request.auth.uid matches compositeId format used in Firestore rules
 */

import {
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signInWithCustomToken
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from './firebase';

// Action code settings for email link
const getActionCodeSettings = () => {
  // Determine the correct URL based on environment
  const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;

  return {
    // URL to redirect to after email link is clicked
    url: `${baseUrl}/auth/email-callback`,
    // This must be true for email link sign-in
    handleCodeInApp: true,
    // iOS app settings (optional - for mobile deep linking)
    // iOS: {
    //   bundleId: 'com.coincommons.app'
    // },
    // Android app settings (optional - for mobile deep linking)
    // android: {
    //   packageName: 'com.coincommons.app',
    //   installApp: true,
    //   minimumVersion: '12'
    // },
    // Dynamic link domain (if using Firebase Dynamic Links)
    // dynamicLinkDomain: 'coincommons.page.link'
  };
};

// Local storage key for email during sign-in flow
const EMAIL_FOR_SIGN_IN_KEY = 'emailForSignIn';

// Storage key for authenticated email user's email address
// (needed because custom token users don't have firebaseUser.email)
const EMAIL_USER_EMAIL_KEY = 'coincommons_email_user_email';

/**
 * Send sign-in link to user's email
 * @param {string} email - User's email address
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const sendEmailSignInLink = async (email) => {
  try {
    const actionCodeSettings = getActionCodeSettings();

    await sendSignInLinkToEmail(auth, email, actionCodeSettings);

    // Save the email locally to complete sign-in when user clicks the link
    window.localStorage.setItem(EMAIL_FOR_SIGN_IN_KEY, email);

    return { success: true };
  } catch (error) {
    console.error('Error sending email sign-in link:', error);

    let errorMessage = 'Failed to send sign-in link. Please try again.';

    switch (error.code) {
      case 'auth/invalid-email':
        errorMessage = 'Please enter a valid email address.';
        break;
      case 'auth/missing-email':
        errorMessage = 'Please enter an email address.';
        break;
      case 'auth/quota-exceeded':
        errorMessage = 'Too many requests. Please try again later.';
        break;
      case 'auth/unauthorized-continue-uri':
        errorMessage = 'Configuration error. Please contact support.';
        break;
      default:
        errorMessage = error.message || errorMessage;
    }

    return { success: false, error: errorMessage };
  }
};

/**
 * Check if the current URL is a sign-in link
 * @returns {boolean}
 */
export const isEmailSignInLink = () => {
  return isSignInWithEmailLink(auth, window.location.href);
};

/**
 * Get the stored email for authenticated email users
 * (Custom token users don't have firebaseUser.email, so we store it separately)
 * @returns {string|null}
 */
export const getStoredEmailUserEmail = () => {
  return window.localStorage.getItem(EMAIL_USER_EMAIL_KEY);
};

/**
 * Clear the stored email user email (call on logout)
 */
export const clearStoredEmailUserEmail = () => {
  window.localStorage.removeItem(EMAIL_USER_EMAIL_KEY);
};

/**
 * Complete the email sign-in process
 *
 * This is a two-step process:
 * 1. Sign in with email link (gets Firebase UID)
 * 2. Get custom token from Cloud Function with e_ prefixed UID (requires Turnstile token)
 * 3. Sign in with custom token (now UID = e_<originalUid>)
 *
 * This ensures request.auth.uid in Firestore rules matches the compositeId format.
 *
 * @param {string} [providedEmail] - Email address (optional, will use stored email if not provided)
 * @param {string} turnstileToken - Turnstile verification token (required)
 * @returns {Promise<{success: boolean, user?: object, error?: string}>}
 */
export const completeEmailSignIn = async (providedEmail = null, turnstileToken = null) => {
  try {
    // Get the email from localStorage or use the provided one
    let email = providedEmail || window.localStorage.getItem(EMAIL_FOR_SIGN_IN_KEY);

    if (!email) {
      // Email not found - user may have opened the link on a different device
      return {
        success: false,
        error: 'Please enter your email address to complete sign-in.',
        needsEmail: true
      };
    }

    // Step 1: Complete the initial email link sign-in
    const result = await signInWithEmailLink(auth, email, window.location.href);
    const originalUid = result.user.uid;

    // Step 2: Call createEmailUserToken to get custom token with e_ prefix
    // This is a separate function with invoker: 'private' for better security
    // Pass the Turnstile token for bot protection
    const createEmailUserToken = httpsCallable(functions, 'createEmailUserToken');
    const tokenResult = await createEmailUserToken({ turnstileToken });

    const { customToken, uid: compositeId, username } = tokenResult.data;

    // Step 3: Sign in with custom token (this will change auth.currentUser.uid to e_<originalUid>)
    await signInWithCustomToken(auth, customToken);

    // Store the email for later retrieval (custom token users don't have firebaseUser.email)
    window.localStorage.setItem(EMAIL_USER_EMAIL_KEY, email);

    // Clear the sign-in flow email (no longer needed)
    window.localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);

    return {
      success: true,
      user: {
        uid: compositeId, // Use the e_ prefixed UID
        originalUid: originalUid, // Keep original UID for reference
        email: result.user.email,
        emailVerified: result.user.emailVerified,
        authMethod: 'email',
        compositeId: compositeId,
        username: username // Auto-generated username (e.g., "CleverPanda4829")
      }
    };
  } catch (error) {
    console.error('Error completing email sign-in:', error);

    let errorMessage = 'Failed to complete sign-in. Please try again.';

    switch (error.code) {
      case 'auth/invalid-action-code':
        errorMessage = 'This sign-in link has expired or already been used. Please request a new one.';
        break;
      case 'auth/expired-action-code':
        errorMessage = 'This sign-in link has expired. Please request a new one.';
        break;
      case 'auth/invalid-email':
        errorMessage = 'The email address does not match the one used to request the link.';
        break;
      case 'auth/user-disabled':
        errorMessage = 'This account has been disabled. Please contact support.';
        break;
      case 'functions/unauthenticated':
        errorMessage = 'Authentication error. Please try again.';
        break;
      default:
        errorMessage = error.message || errorMessage;
    }

    return { success: false, error: errorMessage };
  }
};

/**
 * Get the stored email for sign-in (if any)
 * @returns {string|null}
 */
export const getStoredEmailForSignIn = () => {
  return window.localStorage.getItem(EMAIL_FOR_SIGN_IN_KEY);
};

/**
 * Clear the stored email for sign-in
 */
export const clearStoredEmailForSignIn = () => {
  window.localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);
};
